/**
 * Merges RTDB live entries (trades or transfers) with Firestore historical data.
 *
 * Both AccountTrade and AccountTransfer share { account_id, transaction_id, timestamp }.
 * RTDB may store a single object or an array per key, and returns timestamps as ISO strings.
 */

interface RtdbMergeable {
    account_id: string;
    transaction_id: string;
    timestamp: Date | string;
}

/**
 * RTDB returns timestamps as ISO strings, so the live data shape may differ
 * from the Firestore-hydrated T (where timestamp is already a Date).
 * This mapped type relaxes the timestamp field to accept both Date and string.
 */
type RtdbLive<T> = Omit<T, 'timestamp'> & { timestamp: Date | string };

export function mergeRtdbEntries<T extends RtdbMergeable>(
    base: T[],
    liveData: Record<string, RtdbLive<T> | RtdbLive<T>[]> | null,
    selectedAccount: string | null,
): T[] {
    if (!liveData || !selectedAccount) return base;

    // RTDB may store a single entry or an array — cast to unknown[] at the
    // boundary since the runtime shape may differ from our TypeScript types.
    const entries = Object.values(liveData) as unknown[];
    const liveRaw = entries.find((entry) => {
        if (Array.isArray(entry)) {
            const first = entry[0] as RtdbLive<T> | undefined;
            return first?.account_id === selectedAccount;
        }
        if (typeof entry === 'object' && entry !== null) {
            return (entry as RtdbLive<T>).account_id === selectedAccount;
        }
        return false;
    }) as RtdbLive<T> | RtdbLive<T>[] | undefined;

    if (!liveRaw) return base;

    // Normalize to array and convert RTDB ISO-string timestamps to Date
    const liveEntries = (Array.isArray(liveRaw) ? liveRaw : [liveRaw])
        .map((t) => ({ ...t, timestamp: new Date(t.timestamp as unknown as string) }) as T);

    const existingIds = new Set(base.map(t => t.transaction_id));
    const newEntries = liveEntries.filter((t) => !existingIds.has(t.transaction_id));

    return newEntries.length > 0 ? [...base, ...newEntries] : base;
}
